import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { oportunidadeService } from '@/services/oportunidadeService'
import { OperacaoAnteriorCandidata } from '@/types/oportunidade'
import { STATUS_LABEL } from '@/types/statusReserva'

/**
 * P5 (AN-PP5 §3.6, §9): "É nova operação de uma reserva anterior?" na Nova Reserva.
 *
 * Renovação de Home Equity / Capital de Giro é NOVA operação (comissão única por operação): a
 * reserva nova percorre o funil inteiro e só guarda o vínculo `operacaoAnteriorId`. As opções
 * vêm do backend (reservas ATIVO/ENCERRADA do mesmo CNPJ e produto, no escopo de quem pede); a
 * busca só acontece quando o usuário liga a pergunta. Quem valida é o `POST` (422).
 */

interface Props {
  cnpj: string
  produtoId: string
  /** Modo admin (A11): só as reservas do consultor em nome de quem se registra. */
  consultorId?: string | null
  value: string | null
  onChange: (id: string | null) => void
}

type Estado =
  | { tipo: 'carregando' }
  | { tipo: 'ok'; opcoes: OperacaoAnteriorCandidata[] }
  | { tipo: 'erro'; mensagem: string }

const rotulo = (o: OperacaoAnteriorCandidata): string =>
  [
    o.numero != null ? `#${o.numero}` : null,
    o.status ? STATUS_LABEL[o.status] : null,
    o.razaoSocial ?? null,
  ]
    .filter(Boolean)
    .join(' · ')

export const SeletorOperacaoAnterior: FC<Props> = ({
  cnpj,
  produtoId,
  consultorId = null,
  value,
  onChange,
}) => {
  const [ligado, setLigado] = useState(value !== null)
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' })
  const [tentativa, setTentativa] = useState(0)

  useEffect(() => {
    if (!ligado) return
    let ativo = true
    setEstado({ tipo: 'carregando' })
    oportunidadeService
      .operacoesAnteriores(cnpj, produtoId, consultorId)
      .then((opcoes) => {
        if (ativo) setEstado({ tipo: 'ok', opcoes })
      })
      .catch((e: unknown) => {
        const msg = (e as { message?: unknown } | null)?.message
        if (ativo) {
          setEstado({
            tipo: 'erro',
            mensagem:
              typeof msg === 'string' && msg ? msg : 'Não foi possível carregar as reservas anteriores.',
          })
        }
      })
    return () => {
      ativo = false
    }
  }, [ligado, cnpj, produtoId, consultorId, tentativa])

  const alternar = (on: boolean) => {
    setLigado(on)
    if (!on) onChange(null)
  }

  return (
    <Box component="section" aria-label="Operação anterior" sx={{ mb: 2 }}>
      <FormControlLabel
        control={<Switch checked={ligado} onChange={(e) => alternar(e.target.checked)} />}
        label="É nova operação de uma reserva anterior?"
      />
      {ligado && (
        <Box sx={{ mt: 1 }}>
          {estado.tipo === 'carregando' && (
            <CircularProgress size={20} aria-label="Carregando reservas anteriores" />
          )}
          {estado.tipo === 'erro' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setTentativa((t) => t + 1)}>
                  Tentar novamente
                </Button>
              }
            >
              {estado.mensagem}
            </Alert>
          )}
          {estado.tipo === 'ok' && estado.opcoes.length === 0 && (
            <Alert severity="info">
              Nenhuma reserva ativa ou encerrada deste CNPJ e produto na sua carteira.
            </Alert>
          )}
          {estado.tipo === 'ok' && estado.opcoes.length > 0 && (
            <>
              <TextField
                select
                label="Reserva anterior"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value || null)}
                fullWidth
              >
                {estado.opcoes.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {rotulo(o)}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                Nova operação: novo contrato e nova comissão. Nada é herdado da anterior.
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}
